import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VerificationProcessor = ({ credential, onComplete, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepResults, setStepResults] = useState({});
  const [isProcessing, setIsProcessing] = useState(true);
  const [processingError, setProcessingError] = useState(null);

  const verificationSteps = [
    {
      id: 'format_validation',
      name: 'Format Validation',
      description: 'Validating credential structure and format',
      icon: 'FileCheck',
      duration: 800
    },
    {
      id: 'signature_verification',
      name: 'Digital Signature',
      description: 'Verifying cryptographic signatures',
      icon: 'Key',
      duration: 1200
    },
    {
      id: 'issuer_verification',
      name: 'Issuer Verification',
      description: 'Validating issuer identity and authority',
      icon: 'Building',
      duration: 1000
    },
    {
      id: 'blockchain_anchoring',
      name: 'Blockchain Anchoring',
      description: 'Checking blockchain record and immutability',
      icon: 'Link',
      duration: 1500
    },
    {
      id: 'revocation_check',
      name: 'Revocation Status',
      description: 'Checking if credential has been revoked',
      icon: 'Shield',
      duration: 900
    },
    {
      id: 'expiration_check',
      name: 'Expiration Check',
      description: 'Validating credential validity period',
      icon: 'Calendar',
      duration: 600
    },
    {
      id: 'zk_proof_validation',
      name: 'Zero-Knowledge Proof',
      description: 'Validating privacy-preserving proofs',
      icon: 'Eye',
      duration: 1800
    }
  ];

  const processStep = async (stepIndex) => {
    const step = verificationSteps?.[stepIndex];
    setCurrentStep(stepIndex);

    try {
      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, step.duration));

      // Simulate step results with some randomization for realism
      const success = Math.random() > 0.1; // 90% success rate
      const result = {
        status: success ? 'passed' : 'failed',
        timestamp: new Date()?.toISOString(),
        details: generateStepDetails(step?.id, success),
        processingTime: step?.duration
      };

      setStepResults(prev => ({
        ...prev,
        [step?.id]: result
      }));

      if (!success && step?.id !== 'expiration_check') {
        throw new Error(`${step.name} failed: ${result.details.error}`);
      }

      return result;
    } catch (error) {
      const result = {
        status: 'failed',
        timestamp: new Date()?.toISOString(),
        error: error?.message,
        processingTime: step?.duration
      };

      setStepResults(prev => ({
        ...prev,
        [step?.id]: result
      }));

      throw error;
    }
  };

  const generateStepDetails = (stepId, success) => {
    const details = {
      format_validation: success ? {
        format: 'JSON-LD',
        version: '1.1',
        schema: 'Valid W3C VC Schema',
        contextUrls: ['https://www.w3.org/2018/credentials/v1']
      } : {
        error: 'Invalid credential format',
        issues: ['Missing @context', 'Invalid type field']
      },
      
      signature_verification: success ? {
        algorithm: 'Ed25519Signature2020',
        keyId: 'did:example:issuer#key-1',
        signatureValid: true,
        proofPurpose: 'assertionMethod'
      } : {
        error: 'Signature verification failed',
        reason: 'Invalid signature or tampered data'
      },
      
      issuer_verification: success ? {
        issuerDid: credential?.credential?.issuer || 'did:example:university123',
        issuerName: credential?.credential?.issuer || 'Stanford University',
        trustLevel: 'High',
        registryStatus: 'Active'
      } : {
        error: 'Issuer verification failed',
        reason: 'Issuer not found in trusted registry'
      },
      
      blockchain_anchoring: success ? {
        network: 'Ethereum Mainnet',
        blockNumber: 18745632,
        transactionHash: '0x1a2b3c4d5e6f...',
        confirmations: 1247,
        anchorTime: '2024-01-15T10:00:00Z'
      } : {
        error: 'Blockchain anchor not found',
        reason: 'No matching transaction found'
      },
      
      revocation_check: success ? {
        revocationStatus: 'Not Revoked',
        lastChecked: new Date()?.toISOString(),
        registryUrl: 'https://revocation.example.com/status'
      } : {
        error: 'Credential has been revoked',
        revokedAt: '2024-08-15T14:30:00Z',
        reason: 'Issuer request'
      },
      
      expiration_check: success ? {
        issuanceDate: credential?.credential?.issuanceDate || '2024-01-15',
        expirationDate: credential?.credential?.expirationDate || '2029-01-15',
        daysUntilExpiry: 1825,
        status: 'Valid'
      } : {
        error: 'Credential has expired',
        expiredOn: '2023-12-31',
        daysExpired: 245
      },
      
      zk_proof_validation: success ? {
        proofType: 'BBS+ Signature',
        selectiveDisclosure: true,
        privacyPreserving: true,
        proofValid: true
      } : {
        error: 'Zero-knowledge proof validation failed',
        reason: 'Invalid proof construction'
      }
    };

    return details?.[stepId] || {};
  };

  const runVerification = async () => {
    setIsProcessing(true);
    setProcessingError(null);

    try {
      for (let i = 0; i < verificationSteps?.length; i++) {
        await processStep(i);
      }

      // Complete processing
      setCurrentStep(verificationSteps?.length);
      setIsProcessing(false);

      // Generate final verification result
      const overallResult = generateVerificationResult();
      
      setTimeout(() => {
        onComplete(overallResult);
      }, 1000);

    } catch (error) {
      setProcessingError(error?.message);
      setIsProcessing(false);
    }
  };

  const generateVerificationResult = () => {
    const results = Object.values(stepResults);
    const passedSteps = results?.filter(r => r?.status === 'passed')?.length;
    const failedSteps = results?.filter(r => r?.status === 'failed')?.length;
    
    const overallStatus = failedSteps === 0 ? 'verified' : 
                         failedSteps <= 2 ? 'warning' : 'failed';

    return {
      credentialId: credential?.credential?.id || 'unknown',
      verificationId: `verify_${Date.now()}`,
      timestamp: new Date()?.toISOString(),
      overallStatus,
      confidence: Math.max(0, Math.min(100, (passedSteps / verificationSteps?.length) * 100)),
      stepResults,
      summary: {
        totalSteps: verificationSteps?.length,
        passedSteps,
        failedSteps,
        processingTime: Object.values(stepResults)?.reduce((sum, r) => sum + (r?.processingTime || 0), 0)
      },
      credential: credential?.credential,
      inputMethod: credential?.type || 'unknown'
    };
  };

  useEffect(() => {
    if (credential) {
      runVerification();
    }
  }, [credential]);

  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStep) {
      const stepId = verificationSteps?.[stepIndex]?.id;
      return stepResults?.[stepId]?.status || 'pending';
    } else if (stepIndex === currentStep && isProcessing) {
      return 'processing';
    } else {
      return 'pending';
    }
  };

  const getStepIcon = (step, status) => {
    if (status === 'processing') return 'Loader2';
    if (status === 'passed') return 'CheckCircle';
    if (status === 'failed') return 'XCircle';
    return step?.icon;
  };

  const getStepColor = (status) => {
    switch (status) {
      case 'passed': return 'text-success';
      case 'failed': return 'text-destructive';
      case 'processing': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Icon name="Cog" size={20} className="text-primary mr-2" />
            <h3 className="font-semibold text-foreground">Verification Processing</h3>
          </div>
          <div className="flex items-center space-x-2">
            {isProcessing && (
              <div className="flex items-center text-sm text-muted-foreground">
                <Icon name="Clock" size={16} className="mr-1" />
                Processing...
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              iconName="X"
              iconPosition="left"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
      {/* Credential Info */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={20} className="text-primary" />
          </div>
          <div>
            <h4 className="font-medium text-foreground">
              {credential?.credential?.type || 'Verifiable Credential'}
            </h4>
            <p className="text-sm text-muted-foreground">
              Issuer: {credential?.credential?.issuer || 'Unknown Issuer'}
            </p>
          </div>
        </div>
      </div>
      {/* Verification Steps */}
      <div className="p-4">
        <div className="space-y-4">
          {verificationSteps?.map((step, index) => {
            const status = getStepStatus(index);
            const stepResult = stepResults?.[step?.id];
            
            return (
              <div key={step?.id} className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  status === 'passed' ? 'bg-success/10' :
                  status === 'failed' ? 'bg-destructive/10' :
                  status === 'processing'? 'bg-primary/10' : 'bg-muted'
                }`}>
                  <Icon 
                    name={getStepIcon(step, status)} 
                    size={16} 
                    className={`${getStepColor(status)} ${
                      status === 'processing' ? 'animate-spin' : ''
                    }`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h4 className={`font-medium ${
                      status === 'processing' ? 'text-primary' : 'text-foreground'
                    }`}>
                      {step?.name}
                    </h4>
                    {stepResult && (
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        status === 'passed' ? 'bg-success/10 text-success' :
                        status === 'failed'? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'
                      }`}>
                        {status === 'passed' ? 'Passed' : 
                         status === 'failed' ? 'Failed' : 'Processing'}
                      </span>
                    )}
                  </div>
                  
                  <p className="text-sm text-muted-foreground mt-1">
                    {status === 'processing' ? 'Processing...' : step?.description}
                  </p>
                  
                  {stepResult && stepResult?.error && (
                    <p className="text-sm text-destructive mt-1">
                      {stepResult?.error}
                    </p>
                  )}
                  
                  {stepResult && stepResult?.processingTime && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Completed in {stepResult?.processingTime}ms
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Processing Error */}
        {processingError && (
          <div className="mt-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex items-center">
              <Icon name="AlertCircle" size={20} className="text-destructive mr-2" />
              <div>
                <h4 className="font-medium text-destructive">Verification Failed</h4>
                <p className="text-sm text-destructive/80 mt-1">{processingError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Progress</span>
            <span className="text-sm text-muted-foreground">
              {currentStep}/{verificationSteps?.length} steps
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ 
                width: `${(currentStep / verificationSteps?.length) * 100}%` 
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationProcessor;